import { onMounted, onUnmounted, ref } from 'vue'
import { useMediaQuery } from '@vueuse/core'

export const TYPE_DELAY_MS = 70
export const DELETE_DELAY_MS = 35
export const HOLD_MS = 1729

/**
 * Typed-in, cycling placeholder text for the mobile federated-search trigger
 * button + its detached overlay input (KFT-4035). Types a phrase, holds it
 * for HOLD_MS once fully visible, deletes it, then types the next one —
 * looping forever. `enabled` gates it off entirely on desktop (a real
 * <input placeholder> there instead).
 *
 * `prefers-reduced-motion`: still cycles through the phrases (they're
 * content, not decoration), just swaps them directly instead of
 * typing/deleting.
 */
export const useMobilePlaceholderTypewriter = (
  phrases: string[],
  enabled: boolean
) => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const text = ref(phrases[0] ?? '')
  let timer: ReturnType<typeof setTimeout> | null = null

  // Reduced-motion swap: called once the outer HOLD_MS wait has already
  // elapsed, so it swaps immediately, then schedules the *next* wait.
  const cyclePlain = (index: number) => {
    const next = (index + 1) % phrases.length
    text.value = phrases[next]!
    timer = setTimeout(() => cyclePlain(next), HOLD_MS)
  }

  // Fully typed (or the initial already-visible first phrase): wait HOLD_MS,
  // then start deleting. Applies to every phrase, not just the first.
  const holdThenDelete = (index: number) => {
    timer = setTimeout(
      () => deletePhrase(index, phrases[index]!.length),
      HOLD_MS
    )
  }

  const typePhrase = (index: number, charIndex = 0) => {
    const phrase = phrases[index]!
    text.value = phrase.slice(0, charIndex)
    if (charIndex < phrase.length) {
      timer = setTimeout(() => typePhrase(index, charIndex + 1), TYPE_DELAY_MS)
    } else {
      holdThenDelete(index)
    }
  }

  const deletePhrase = (index: number, charIndex: number) => {
    const phrase = phrases[index]!
    text.value = phrase.slice(0, charIndex)
    if (charIndex > 0) {
      timer = setTimeout(
        () => deletePhrase(index, charIndex - 1),
        DELETE_DELAY_MS
      )
    } else {
      typePhrase((index + 1) % phrases.length)
    }
  }

  onMounted(() => {
    if (!enabled || phrases.length < 2) return
    if (prefersReducedMotion.value) {
      timer = setTimeout(() => cyclePlain(0), HOLD_MS)
    } else {
      holdThenDelete(0)
    }
  })

  onUnmounted(() => {
    if (timer) clearTimeout(timer)
  })

  return { text }
}
